/**
 * #1231 — which Polymarket US line quote a Spread/Total row shows.
 *
 * The join is the ticket's own: `pickUsSpread` (the favored team at −m, or
 * the other team at +m as the complement) and `pickUsFullGameTotal` (the
 * sport's native family at the exact threshold). Nothing is matched by a
 * title or a nearby line.
 *
 * Three cell states (#1231: "absent, not blank"):
 *   absent   no single exact US market for this line → an empty cell;
 *   listed   the market is held but its quote is not current → disabled pills
 *            with a reason, as K/P show a stale book in the same row;
 *   current  a current quote → priced pills.
 */
import { pickUsSpread, usdToCents, usGameKeyForBoardKey, usOutcomeFor } from '@/lib/polymarketUs/gameMapping';
import { usGameSportForKey } from '@/lib/polymarketUs/sports';
import { pickUsFullGameTotal, usFullGameTotalFamilyForSport, validUsTotalThreshold } from '@/lib/polymarketUs/totalTarget';
import type { UsBrowserLineQuote } from '@/stores/usBoardQuotes';
import type { MarketSide, PriceMode } from '../ValueDashboard/types';

export type UsLineCell =
  | { state: 'absent' }
  | { state: 'awaiting' }
  | { state: 'listed' | 'current'; quote: UsBrowserLineQuote; outcomeFor: (boardSide: MarketSide) => 'yes' | 'no' };

const ABSENT: UsLineCell = { state: 'absent' };
const AWAITING: UsLineCell = { state: 'awaiting' };
type ListedLine = { usDisplay?: { marketSlugs: readonly string[] } };
const noQuote = (row: ListedLine): UsLineCell => row.usDisplay?.marketSlugs.length ? AWAITING : ABSENT;

function cell(quote: UsBrowserLineQuote, outcomeFor: (boardSide: MarketSide) => 'yes' | 'no'): UsLineCell {
  return { state: quote.currentAt() ? 'current' : 'listed', quote, outcomeFor };
}

/** A board spread row: `line` is signed from the away team's view, `favored` the covering side. */
export function usSpreadCell(
  event: { key: string; awayCode: string; homeCode: string },
  row: { line: number; favored: 'away' | 'home' } & ListedLine,
  candidates: readonly UsBrowserLineQuote[],
): UsLineCell {
  const usKey = usGameKeyForBoardKey(event.key);
  const magnitude = Math.abs(row.line);
  if (!usKey || !(magnitude > 0)) return ABSENT;
  const favoredCode = row.favored === 'away' ? event.awayCode : event.homeCode;
  const otherCode = row.favored === 'away' ? event.homeCode : event.awayCode;
  const pick = pickUsSpread(candidates, usKey, { favoredCode, otherCode, magnitude });
  if (pick.kind !== 'market' || !pick.market.yesTeam) return noQuote(row);
  const yesTeam = pick.market.yesTeam;
  // Board YES = "favored wins by more than m": US YES when the favored team is
  // the US YES team, its complement when the US market is the other team +m.
  return cell(pick.market, (boardSide) => usOutcomeFor(favoredCode, yesTeam, boardSide));
}

/** A board total row: YES is Over, which is the native total's YES. */
export function usTotalCell(
  event: { key: string },
  row: { line: number } & ListedLine,
  candidates: readonly UsBrowserLineQuote[],
): UsLineCell {
  const usKey = usGameKeyForBoardKey(event.key);
  const family = usKey ? usFullGameTotalFamilyForSport(usGameSportForKey(usKey)) : null;
  if (!usKey || !family || !validUsTotalThreshold(row.line)) return ABSENT;
  const pick = pickUsFullGameTotal(candidates, usKey, { family, threshold: row.line });
  if (pick.kind !== 'market') return noQuote(row);
  return cell(pick.market, (boardSide) => boardSide);
}

/** Cents a US line pill shows for a board side, or null (absent, not current, or market mode). */
export function usLinePillCents(c: UsLineCell, boardSide: MarketSide, priceMode: PriceMode): number | null {
  if (c.state !== 'current' || priceMode === 'market' || !c.quote.currentAt()) return null;
  const outcome = c.outcomeFor(boardSide), b = c.quote.book;
  const usd = priceMode === 'maker'
    ? (outcome === 'yes' ? b.yesBidUsd : b.noBidUsd)
    : (outcome === 'yes' ? b.yesAskUsd : b.noAskUsd);
  return usd === null ? null : usdToCents(usd);
}
